import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { PortalContainerContext } from "@/components/ui/dialog"

// Touch devices have no hover: tooltips open on tap as a bubble with a close button.
const isTouch = typeof window !== "undefined" && window.matchMedia?.("(hover: none), (pointer: coarse)").matches

const TouchProvider = ({ children }) => <>{children}</>

const TouchContent = React.forwardRef(({ className, children, sideOffset = 6, ...props }, ref) => (
  <PopoverPrimitive.Portal container={React.useContext(PortalContainerContext) || undefined}>
    <PopoverPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={12}
      className={cn(
        "z-50 relative max-w-[min(20rem,calc(100vw-1.5rem))] rounded-xl bg-primary p-4 pr-10 text-sm leading-relaxed text-primary-foreground shadow-xl animate-in fade-in-0 zoom-in-95",
        className
      )}
      {...props}>
      {children}
      <PopoverPrimitive.Close
        aria-label="Cerrar"
        data-testid="tooltip-close"
        className="absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-full opacity-80 hover:opacity-100">
        <X className="h-4 w-4" />
      </PopoverPrimitive.Close>
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
))
TouchContent.displayName = "TouchTooltipContent"

const DesktopContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props} />
  </TooltipPrimitive.Portal>
))
DesktopContent.displayName = TooltipPrimitive.Content.displayName

const TooltipProvider = isTouch ? TouchProvider : TooltipPrimitive.Provider
const Tooltip = isTouch ? PopoverPrimitive.Root : TooltipPrimitive.Root
const TooltipTrigger = isTouch ? PopoverPrimitive.Trigger : TooltipPrimitive.Trigger
const TooltipContent = isTouch ? TouchContent : DesktopContent

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
